import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { DURUM_RENKLERI } from '@/lib/constants';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts';

export default function GelirRaporlariPage() {
  useAuthGuard();
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState<any[]>([]);
  const [donem, setDonem] = useState<'gunluk' | 'haftalik' | 'aylik'>('gunluk');
  const [error, setError] = useState('');

  useEffect(() => { setMounted(true); }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('reservations')
      .select('id,rezervasyon_no,musteri_adi,gelis_tarihi,alinis_yeri,birak_yeri,ucret,para_birimi,durum,ilce,atanan_arac')
      .order('gelis_tarihi', { ascending: false });
    if (err) setError(err.message);
    else setReservations(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const aktif = useMemo(() => reservations.filter(r => r.durum !== 'İptal Edildi'), [reservations]);

  const { toplamEUR, toplamUSD, toplamTL } = useMemo(() => ({
    toplamEUR: aktif.filter(r => r.para_birimi === 'EUR').reduce((s: number, r: any) => s + Number(r.ucret), 0),
    toplamUSD: aktif.filter(r => r.para_birimi === 'USD').reduce((s: number, r: any) => s + Number(r.ucret), 0),
    toplamTL: aktif.filter(r => r.para_birimi === 'TL').reduce((s: number, r: any) => s + Number(r.ucret), 0),
  }), [aktif]);

  const donemRez = useMemo(() => {
    const now = new Date();
    if (donem === 'gunluk') {
      const today = now.toISOString().split('T')[0];
      return aktif.filter(r => r.gelis_tarihi === today);
    }
    if (donem === 'haftalik') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      return aktif.filter(r => r.gelis_tarihi >= weekAgo);
    }
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return aktif.filter(r => r.gelis_tarihi >= monthAgo);
  }, [aktif, donem]);

  const chartData = useMemo(() => {
    const days: Record<string, { tarih: string; EUR: number; USD: number }> = {};
    const count = donem === 'gunluk' ? 7 : donem === 'haftalik' ? 14 : 30;
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const label = new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
      days[d] = { tarih: label, EUR: 0, USD: 0 };
    }
    aktif.forEach((r: any) => {
      if (days[r.gelis_tarihi]) {
        if (r.para_birimi === 'EUR') days[r.gelis_tarihi].EUR += Number(r.ucret);
        if (r.para_birimi === 'USD') days[r.gelis_tarihi].USD += Number(r.ucret);
      }
    });
    return Object.values(days);
  }, [aktif, donem]);

  const ilceData = useMemo(() => {
    const map: Record<string, number> = {};
    aktif.forEach((r: any) => {
      if (r.ilce && r.para_birimi === 'EUR') {
        map[r.ilce] = (map[r.ilce] || 0) + Number(r.ucret);
      }
    });
    return Object.entries(map).map(([ilce, kazanc]) => ({ ilce, kazanc })).sort((a, b) => b.kazanc - a.kazanc).slice(0, 10);
  }, [aktif]);

  const aracData = useMemo(() => {
    const map: Record<string, number> = {};
    aktif.forEach((r: any) => {
      if (r.atanan_arac) {
        const eur = r.para_birimi === 'EUR' ? Number(r.ucret) : r.para_birimi === 'USD' ? Number(r.ucret) * 0.92 : Number(r.ucret) / 38;
        map[r.atanan_arac] = (map[r.atanan_arac] || 0) + eur;
      }
    });
    return Object.entries(map).map(([arac, kazanc]) => ({ arac, kazanc: Math.round(kazanc) })).sort((a, b) => b.kazanc - a.kazanc).slice(0, 8);
  }, [aktif]);

  const paraBirimiData = [
    { name: 'EUR', value: toplamEUR, color: '#C8A45C' },
    { name: 'USD', value: toplamUSD, color: '#60A5FA' },
    { name: 'TL', value: toplamTL, color: '#34D399' },
  ].filter(d => d.value > 0);

  const tooltipStyle = { backgroundColor: '#1F2937', border: '1px solid rgba(200,164,92,0.2)', borderRadius: '8px', color: '#fff', fontSize: '12px' };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-display font-bold text-white">Gelir / Ciro Raporları</h2>
        <p className="text-sm text-gray-400 mt-1">Detaylı gelir analizleri ve para birimi dağılımı</p>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-red-400"><i className="ri-error-warning-line mr-2"></i>{error}</div>}

      <div className="flex gap-1 bg-vip-dark border border-vip-gold/10 rounded-lg p-1 mb-6 w-fit">
        {(['gunluk','haftalik','aylik'] as const).map(d => (
          <button key={d} onClick={() => setDonem(d)}
            className={`px-4 py-2 text-xs font-medium rounded-md transition-all whitespace-nowrap ${donem === d ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20' : 'text-gray-400 hover:text-white'}`}>
            {d === 'gunluk' ? 'Günlük' : d === 'haftalik' ? 'Haftalık' : 'Aylık'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-3 gap-4 mb-6">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-vip-dark rounded-lg animate-pulse"></div>)}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-lg bg-vip-gold/15 flex items-center justify-center"><i className="ri-money-euro-circle-line text-vip-gold text-sm"></i></div>
                <div>
                  <p className="text-xs text-gray-400">Dönem Ciro (EUR)</p>
                  <p className="text-xl font-bold text-vip-gold">{donemRez.filter(r => r.para_birimi === 'EUR').reduce((s: number, r: any) => s + Number(r.ucret), 0)} €</p>
                </div>
              </div>
            </div>
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center"><i className="ri-money-dollar-circle-line text-blue-400 text-sm"></i></div>
                <div>
                  <p className="text-xs text-gray-400">Dönem Ciro (USD)</p>
                  <p className="text-xl font-bold text-blue-400">{donemRez.filter(r => r.para_birimi === 'USD').reduce((s: number, r: any) => s + Number(r.ucret), 0)} $</p>
                </div>
              </div>
            </div>
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center"><i className="ri-money-cny-circle-line text-emerald-400 text-sm"></i></div>
                <div>
                  <p className="text-xs text-gray-400">Toplam TL</p>
                  <p className="text-xl font-bold text-emerald-400">{toplamTL.toLocaleString('tr-TR')} ₺</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Kazanç Grafiği (EUR / USD)</h3>
              {mounted && (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                    <XAxis dataKey="tarih" stroke="#6B7280" fontSize={11} angle={-30} textAnchor="end" height={50} />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line type="monotone" dataKey="EUR" stroke="#C8A45C" strokeWidth={2} dot={{ fill: '#C8A45C', r: 3 }} />
                    <Line type="monotone" dataKey="USD" stroke="#60A5FA" strokeWidth={2} dot={{ fill: '#60A5FA', r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Para Birimi Dağılımı</h3>
              {mounted && paraBirimiData.length > 0 && (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={paraBirimiData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                      {paraBirimiData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-4">İlçeye Göre Ciro (EUR)</h3>
              {mounted && (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={ilceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                    <XAxis dataKey="ilce" stroke="#6B7280" fontSize={11} angle={-30} textAnchor="end" height={60} />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="kazanc" fill="#C8A45C" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Araç Bazlı Kazanç (EUR Eşdeğer)</h3>
              {mounted && (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={aracData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                    <XAxis dataKey="arac" stroke="#6B7280" fontSize={11} angle={-30} textAnchor="end" height={60} />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="kazanc" fill="#60A5FA" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Rezervasyon Gelir Özeti ({donemRez.length} kayıt)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-vip-gold/10">
                    <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Rez. No</th>
                    <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Müşteri</th>
                    <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Tarih</th>
                    <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Rota</th>
                    <th className="text-center py-2.5 px-3 text-gray-400 font-medium">Durum</th>
                    <th className="text-right py-2.5 px-3 text-gray-400 font-medium">Ücret</th>
                  </tr>
                </thead>
                <tbody>
                  {donemRez.slice(0, 50).map(r => (
                    <tr key={r.id} className="border-b border-vip-gold/5 hover:bg-white/5 transition-colors">
                      <td className="py-2.5 px-3 text-vip-gold font-mono text-xs">{r.rezervasyon_no}</td>
                      <td className="py-2.5 px-3 text-white">{r.musteri_adi}</td>
                      <td className="py-2.5 px-3 text-gray-400">{r.gelis_tarihi}</td>
                      <td className="py-2.5 px-3 text-gray-400 text-xs">{r.alinis_yeri} → {r.birak_yeri}</td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DURUM_RENKLERI[r.durum] || 'text-gray-400 border-gray-500/30'}`}>{r.durum}</span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-vip-gold">{r.ucret} {r.para_birimi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}